if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.form.querySelector('[name=id]').disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');

        // CRITICAL: For bundles, prevent UpCart from hijacking the submission
        const isBundlePdp = Boolean(document.querySelector('.bundle-container'));
        if (isBundlePdp && this.submitButton) {
          this.submitButton.addEventListener('click', (evt) => {
            evt.stopPropagation(); // Stop UpCart's listeners
            // The form submit event will still fire and our handler will process it
          }, true); // Capture phase - runs BEFORE UpCart's bubble-phase listeners
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.bundleSubmitInitiallyDisabled = this.submitButton
          ? this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true'
          : false;
        this.bundleTotalsCallback = (event) => {
          this.updateBundleSubmitState(event.detail);
        };
        document.addEventListener('bundle:totals-changed', this.bundleTotalsCallback);
        if (typeof window.currentBundleTotals !== 'undefined') {
          this.updateBundleSubmitState(window.currentBundleTotals);
        }
      }

      onSubmitHandler(evt) {
        evt.preventDefault();

        // CRITICAL: Check for bundle IMMEDIATELY to prevent UpCart interception
        const isBundlePdp = Boolean(document.querySelector('.bundle-container'));
        if (isBundlePdp) {
          evt.stopImmediatePropagation(); // Stop UpCart and other listeners
        }

        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        const loadingSpinner = this.querySelector('.loading__spinner');
        if (loadingSpinner) loadingSpinner.classList.remove('hidden');

        const resetSubmissionState = () => {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          if (loadingSpinner) loadingSpinner.classList.add('hidden');
          this.error = false;
          if (!this.bundleSubmitInitiallyDisabled && this.submitButton.dataset.bundleDisabled !== 'true') {
            this.submitButton.disabled = false;
          }
          this.updateBundleSubmitState(window.currentBundleTotals || null);
        };

        const markDropdownErrors = (keys = []) => {
          if (!Array.isArray(keys) || !keys.length) return;
          keys.forEach((key) => {
            const dropdown = document.querySelector(`.bundle-dropdown[data-component-key="${key}"]`);
            if (dropdown) dropdown.classList.add('bundle-dropdown--error');
          });
        };

        const processRequest = (requestPromise, variantIdForEvent = null) => {
          requestPromise
            .then((response) => response.json())
            .then((response) => {
              if (response.status) {
                publish(PUB_SUB_EVENTS.cartError, {
                  source: 'product-form',
                  productVariantId: variantIdForEvent,
                  errors: response.errors || response.description,
                  message: response.message,
                });
                this.handleErrorMessage(response.description);

                const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
                if (!soldOutMessage) return;
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButton.querySelector('span').classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
                this.error = true;
                return;
              }

              if (!this.error) {
                // For bundle products, don't publish cartUpdate to avoid apps clearing the cart
                const isBundleProduct = Boolean(document.querySelector('.bundle-container'));
                if (!isBundleProduct) {
                  publish(PUB_SUB_EVENTS.cartUpdate, {
                    source: 'product-form',
                    productVariantId: variantIdForEvent,
                    cartData: response,
                  });
                }
              }
                this.error = false;
                const quickAddModal = this.closest('quick-add-modal');
                if (quickAddModal) {
                  document.body.addEventListener(
                    'modalClosed',
                  () => {
                    setTimeout(() => {
                      if (this.cart) this.cart.renderContents(response);
                    });
                  },
                  { once: true }
                );
                quickAddModal.hide(true);
              } else {
                if (this.cart) this.cart.renderContents(response);
              }
              document.querySelectorAll('.bundle-dropdown--error').forEach((dropdown) => {
                dropdown.classList.remove('bundle-dropdown--error');
              });
              this.handleErrorMessage();
            })
            .catch((e) => {
              console.error(e);
              this.error = true;
              this.handleErrorMessage('Something went wrong while adding to cart. Please try again.');
              resetSubmissionState();
            })
            .finally(() => {
              this.submitButton.classList.remove('loading');
              if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              if (!this.error) this.submitButton.removeAttribute('aria-disabled');
              if (loadingSpinner) loadingSpinner.classList.add('hidden');
            });
        };

        // isBundlePdp already detected at top of onSubmitHandler
        const bundleStateGetter = typeof window.getBundleSelectionState === 'function' ? window.getBundleSelectionState : null;
        const bundleState = bundleStateGetter ? bundleStateGetter() : null;

        if (isBundlePdp) {
          document.querySelectorAll('.bundle-dropdown--error').forEach((dropdown) => {
            dropdown.classList.remove('bundle-dropdown--error');
          });

          if (!bundleState) {
            this.error = true;
            this.handleErrorMessage('Bundle configuration is still loading. Please refresh the page and try again.');
            resetSubmissionState();
            return;
          }

          const {
            selections = {},
            isComplete,
            missingComponents = [],
            planName = '',
            purchaseType = 'subscribe'
          } = bundleState;
          const isSubscribeFlow = purchaseType === 'subscribe';
          const selectionValues = Object.values(selections);

          if (!selectionValues.length || !isComplete) {
            this.error = true;
            markDropdownErrors(missingComponents);
            this.handleErrorMessage('Please select a variant for each bundle component before adding to cart.');
            resetSubmissionState();
            return;
          }

          const planLabel = planName.trim();
          if (isSubscribeFlow && !planLabel) {
            this.error = true;
            this.handleErrorMessage('Please select a subscription plan to continue.');
            resetSubmissionState();
            return;
          }

          const items = [];
          let sellingPlanIssue = null;

          selectionValues.forEach((selection) => {
            if (sellingPlanIssue) return;

            const quantity = Number(selection.quantity || 1);
            const variantIdNumber = Number(selection.variantId);

            if (!variantIdNumber) {
              sellingPlanIssue = {
                message: 'Bundle configuration is missing variant information. Please refresh the page and try again.',
              };
              return;
            }

            const sellingPlanMap = selection.sellingPlans || {};
            const sellingPlanId = sellingPlanMap[planLabel];

            if (isSubscribeFlow && !sellingPlanId) {
              sellingPlanIssue = {
                message: `The selected plan is unavailable for ${selection.componentTitle || 'one of the bundle products'}. Please choose another option.`,
                key: selection.componentKey,
              };
              return;
            }

            const item = {
              id: variantIdNumber,
              quantity: Number.isNaN(quantity) || quantity <= 0 ? 1 : quantity
            };

            // Always include bundle properties for both subscription and one-time purchases
            if (selection.bundleId) {
              item.properties = {
                __bundle_id: selection.bundleId,
                __bundle_component: selection.componentKey || 'unknown',
                __bundle_name: window.bundleId || selection.bundleId,
                __bundle_product_title: window.bundleProductTitle || '',
                __bundle_product_image: window.bundleProductImage || ''
              };
            }

            if (isSubscribeFlow && sellingPlanId) {
              item.selling_plan = sellingPlanId;
            }

            items.push(item);
          });

          if (sellingPlanIssue) {
            this.error = true;
            if (sellingPlanIssue.key) {
              markDropdownErrors([sellingPlanIssue.key]);
            }
            this.handleErrorMessage(sellingPlanIssue.message);
            resetSubmissionState();
            return;
          }

          if (!items.length) {
            this.error = true;
            this.handleErrorMessage('Bundle configuration is incomplete. Please refresh the page and try again.');
            resetSubmissionState();
            return;
          }

          // Add gift product if configured
          // Gift is added for both one-time and subscription purchases
          // For subscriptions, gift has NO selling plan, so it only appears in first delivery
          if (window.bundleGiftProduct && selectionValues.length > 0) {
            const firstSelection = selectionValues[0];
            const giftVariantIdNumber = Number(window.bundleGiftProduct.variantId);

            if (giftVariantIdNumber && firstSelection.bundleId) {
              const giftItem = {
                id: giftVariantIdNumber,
                quantity: 1,
                properties: {
                  __bundle_id: firstSelection.bundleId,
                  __bundle_gift: firstSelection.bundleId,
                  __bundle_name: window.bundleId || firstSelection.bundleId,
                  __bundle_product_title: window.bundleProductTitle || '',
                  __bundle_product_image: window.bundleProductImage || ''
                }
              };

              // NOTE: No selling plan is attached to gift - this ensures it only appears in first delivery for subscriptions
              items.push(giftItem);
            }
          }

          const payload = { items };

          if (this.cart) {
            const sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections = sections.join(',');
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }

          // Use XMLHttpRequest to bypass UpCart's fetch wrapper
          const xhr = new XMLHttpRequest();
          xhr.open('POST', routes.cart_add_url, true);
          xhr.setRequestHeader('Content-Type', 'application/json');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.setRequestHeader('Accept', 'application/json');

          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                processRequest(Promise.resolve({
                  json: () => Promise.resolve(response),
                  status: xhr.status,
                  ok: true
                }), String(items[0]?.id || ''));
              } catch (e) {
                resetSubmissionState();
              }
            } else {
              resetSubmissionState();
            }
          };

          xhr.onerror = function() {
            resetSubmissionState();
          };

          xhr.send(JSON.stringify(payload));
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        processRequest(fetch(`${routes.cart_add_url}`, config), formData.get('id'));
      }

      updateBundleSubmitState(snapshot) {
        if (!this.submitButton) return;

        const isComplete = snapshot && snapshot.isComplete !== false;

        if (!isComplete) {
          this.submitButton.dataset.bundleDisabled = 'true';
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('product-form__submit--bundle-disabled');
        } else {
          if (this.submitButton.dataset.bundleDisabled === 'true') {
            delete this.submitButton.dataset.bundleDisabled;
            this.submitButton.classList.remove('product-form__submit--bundle-disabled');
            if (!this.bundleSubmitInitiallyDisabled) {
              this.submitButton.disabled = false;
              this.submitButton.removeAttribute('aria-disabled');
            }
          }
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      disconnectedCallback() {
        if (this.bundleTotalsCallback) {
          document.removeEventListener('bundle:totals-changed', this.bundleTotalsCallback);
        }
      }
    }
  );
}
